import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useGetAllowedOrganizationOfferGroups,
  useGetAllowedOrganizationTags,
  useGetBobcatTagsList,
  useListBobcatOfferGroups,
} from '@/api/trafikportalen';
import type { CollectionTypeOptInDTO } from '@/api/trafikportalen/models/collectionTypeOptInDTO';
import { collator } from '@/utils/collator';
import { OrganizationPeriodAccess } from './OrganizationPeriodAccess';
import { StudentCategorySettings } from './StudentCategorySettings';
import type { OrganizationDTO } from '@/api/trafikportalen';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';

interface OrganizationStudentSettingsProps {
  organization: OrganizationDTO;
}

export const OrganizationStudentSettings = ({
  organization,
}: OrganizationStudentSettingsProps) => {
  const { formatMessage } = useIntl();
  const { disableHalfProduct, disableDiscountCodes, studentCategoryOrder } =
    useTenantConfiguration();
  const enabledTypes = organization.enabled_collection_types ?? [];

  const { data: allOfferGroups, isLoading: isLoadingOfferGroups } =
    useListBobcatOfferGroups();
  const { data: allTags, isLoading: isLoadingTags } = useGetBobcatTagsList();
  const { data: allowedOfferGroups, isLoading: isLoadingAllowedOfferGroups } =
    useGetAllowedOrganizationOfferGroups(organization.id);
  const { data: allowedTags, isLoading: isLoadingAllowedTags } =
    useGetAllowedOrganizationTags(organization.id);

  const productClassTags = useMemo(() => {
    if (!allTags) return [];
    return allTags.filter((tag) => tag.tag_id.startsWith('pc_'));
  }, [allTags]);

  const tagItems = useMemo(() => {
    return productClassTags.map((tag) => ({
      id: tag.id,
      title: tag.title,
    }));
  }, [productClassTags]);

  const schoolOfferGroupItems = useMemo(() => {
    if (!allOfferGroups) return [];
    return allOfferGroups
      .filter((og) => og.sales_channel === 'school')
      .map((offer) => {
        const productClassTag = productClassTags.find(
          (tag) => tag.tag_id === offer.product_class,
        );
        return {
          id: offer.id,
          title: offer.title,
          subtitle: productClassTag?.title,
          groupKey: offer.group_key,
        };
      })
      .sort((a, b) => collator.compare(a.title, b.title));
  }, [allOfferGroups, productClassTags]);

  const allowedOfferGroupIdsByType = useMemo(() => {
    if (!allowedOfferGroups) return {} as Record<string, string[]>;
    const result: Record<string, string[]> = {};
    for (const og of allowedOfferGroups) {
      if (!result[og.collection_type]) result[og.collection_type] = [];
      result[og.collection_type].push(og.id);
    }
    return result;
  }, [allowedOfferGroups]);

  const allowedTagIdsByType = useMemo(() => {
    if (!allowedTags) return {} as Record<string, string[]>;
    const result: Record<string, string[]> = {};
    for (const tag of allowedTags) {
      if (!tag.tag_id.startsWith('pc_')) continue;
      if (!result[tag.collection_type]) result[tag.collection_type] = [];
      result[tag.collection_type].push(tag.id);
    }
    return result;
  }, [allowedTags]);

  const isLoadingOfferGroupData =
    isLoadingOfferGroups || isLoadingAllowedOfferGroups;
  const isLoadingTagData = isLoadingTags || isLoadingAllowedTags;

  const categoryTexts: Record<
    CollectionTypeOptInDTO,
    { title: string; description: React.ReactNode }
  > = {
    'school-student-travel': {
      title: formatMessage({ defaultMessage: 'Elevresor' }),
      description: (
        <FormattedMessage defaultMessage="Reser till och från gymnasiet. Tänkt för relationsbundna biljetter." />
      ),
    },
    'school-transportation': {
      title: formatMessage({ defaultMessage: 'Skolskjuts' }),
      description: (
        <FormattedMessage defaultMessage="Lagstadgade resor till och från grundskolan. Tänkt för relationsbundna biljetter." />
      ),
    },
    'school-child-and-youth': {
      title: formatMessage({ defaultMessage: 'Barn- & Ungdomskort' }),
      description: (
        <FormattedMessage defaultMessage="Statiska biljetter för barn och ungdomar. Simplare system: Enbart epost för vårdnadshavare och enbart namn för skola." />
      ),
    },
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <OrganizationPeriodAccess organization={organization} />
      {studentCategoryOrder.map((collectionType) => (
        <StudentCategorySettings
          key={collectionType}
          organization={organization}
          collectionType={collectionType}
          title={categoryTexts[collectionType].title}
          description={categoryTexts[collectionType].description}
          enabledTypes={enabledTypes}
          offerGroupItems={schoolOfferGroupItems}
          allowedOfferGroupIds={
            allowedOfferGroupIdsByType[collectionType] ?? []
          }
          isLoadingOfferGroups={isLoadingOfferGroupData}
          tagItems={tagItems}
          allowedTagIds={allowedTagIdsByType[collectionType] ?? []}
          isLoadingTags={isLoadingTagData}
          disableHalfProduct={disableHalfProduct}
          disableDiscountCodes={disableDiscountCodes}
        />
      ))}
    </div>
  );
};
